import { JSX, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, CircularProgress, Grid2, Stack, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { ModelListRowModel, PhotosToSave } from '~/features/models/models-list/types'
import { modelSchemaBase, ModelSchemaT } from '../../validators/modelValidators'
import { ControlledTextField } from '~/ui/components/ControlledTextField'
import { ControlledAutocomplete } from '~/ui/components/ControlledAutocomplete'
import { usePatchModels } from '../../hooks/usePatchModels'
import { StatusChip } from '../../components/StatusChip'
import { ZodType } from 'zod'
import { ModelPhotos } from '../ModelPhotos'
import { useRenameModelPhoto } from '../../hooks/useRenameModelPhoto'
import { toStoragePath } from '~/supabase/bucket/api'
import { toast } from 'sonner'
import { GET_BUCKET_PHOTOS_QUERY_KEY } from '../../hooks/useBucketPhotos'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateActions } from '../../hooks/useCreateActions'

const Tile = ({ caption, text }: { caption: string; text: string | JSX.Element }) => {
  return (
    <Stack
      pb={1}
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      {typeof text === 'string' ? <Typography variant="subtitle2">{text}</Typography> : text}
    </Stack>
  )
}

type FormData = Pick<
  ModelSchemaT,
  | 'diamond_ct'
  | 'garnet_ct'
  | 'material'
  | 'price'
  | 'weight'
  | 'shopify_product_id'
  | 'supplier_code'
>
const ModelDetailForm = ({
  data,
  photosToSave,
  resetPhotosToSave,
  isDisabled,
}: {
  isDisabled: boolean
  data: ModelListRowModel
  photosToSave: PhotosToSave
  resetPhotosToSave: () => void
}) => {
  const { mutate: renameModelPhoto, isPending: isRenamingModelPhoto } = useRenameModelPhoto()
  const queryClient = useQueryClient()

  const {
    supplier_code: supplierCode,
    material,
    diamond_ct,
    garnet_ct,
    price,
    weight,
    shopify_product_id,
    images,
  } = data
  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      diamond_ct,
      garnet_ct,
      material,
      price,
      weight,
      shopify_product_id,
      supplier_code: supplierCode,
    },
    resolver: zodResolver(
      modelSchemaBase.pick({
        diamond_ct: true,
        garnet_ct: true,
        material: true,
        price: true,
        weight: true,
        shopify_product_id: true,
        supplier_code: true,
      }) satisfies ZodType<FormData>
    ),
  })

  const { mutate: patchModels, isPending: isPatchingModels } = usePatchModels()
  const { mutate: createAction, isPending: isCreatingAction } = useCreateActions()

  const onSubmit = (payload: FormData) => {
    const updateModel = () => {
      patchModels([
        {
          ...payload,
          id: data.id,
          status_v2: !images
            ? 'incomplete'
            : images.length === 0
              ? 'incomplete'
              : shopify_product_id
                ? 'in_eshop'
                : 'ready',
        },
      ])
    }

    const applyModelUpdate = () => {
      if (dirtyFields.price) {
        createAction(
          [
            {
              model: data.stock_code,
              type: 'price_change',
              old_value: data.price,
              new_value: payload.price,
            },
          ],
          { onSuccess: updateModel }
        )
      } else updateModel()
    }

    const buildRenameOps = () => {
      if (photosToSave && dirtyFields.supplier_code) {
        return photosToSave.map((photo) => ({
          sourcePath: toStoragePath({ supplierCode, material, filename: photo.oldPath }),
          destinationPath: toStoragePath({
            supplierCode: payload.supplier_code,
            material,
            filename: photo.newPath,
          }),
        }))
      }
      if (photosToSave) {
        return photosToSave.map((photo) => ({
          sourcePath: toStoragePath({ supplierCode, material, filename: photo.oldPath }),
          destinationPath: toStoragePath({ supplierCode, material, filename: photo.newPath }),
        }))
      }
      if (dirtyFields.supplier_code) {
        return (data.images ?? []).map((image) => ({
          sourcePath: toStoragePath({
            supplierCode: image.supplierCode,
            material: image.material,
            filename: image.fileName,
          }),
          destinationPath: toStoragePath({
            supplierCode: payload.supplier_code,
            material: image.material,
            filename: image.fileName,
          }),
        }))
      }
      return []
    }

    const renameOps = buildRenameOps()

    if (renameOps.length > 0) {
      renameModelPhoto(renameOps, {
        onSuccess: () => {
          applyModelUpdate()
          resetPhotosToSave()
          queryClient.invalidateQueries({ queryKey: [GET_BUCKET_PHOTOS_QUERY_KEY] })
          if (photosToSave) toast.success('Photos reordered successfully')
        },
        onError: () => {
          toast.error('Failed to rename photos. Please try again.')
        },
      })
    } else {
      applyModelUpdate()
    }
  }

  const isLoading = isPatchingModels || isRenamingModelPhoto || isCreatingAction

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      useFlexGap
      sx={{ overflow: 'auto', flexGrow: 1, py: 1 }}
    >
      <Stack direction="row" spacing={2} useFlexGap>
        <ControlledTextField
          control={control}
          name="diamond_ct"
          label="Diamond ct"
          errors={errors}
          disabled={!data.material.includes('diamond') || isDisabled}
        />
        <ControlledTextField
          control={control}
          name="garnet_ct"
          label="Garnet ct"
          errors={errors}
          disabled={isDisabled}
        />
        <ControlledTextField control={control} name="weight" label="Weight" errors={errors} />
      </Stack>
      <Stack direction="row" spacing={2} useFlexGap>
        <ControlledTextField
          control={control}
          name="supplier_code"
          label="Supplier Code"
          errors={errors}
        />
        <ControlledTextField
          control={control}
          name="price"
          label="Price"
          errors={errors}
          disabled={data.status_v2 === 'in_eshop' && !data.shopify_product_id}
        />
      </Stack>
      <ControlledAutocomplete
        control={control}
        name="shopify_product_id"
        label={data.shopifyProduct ? 'Shopify product id' : 'Shopify product is missing'}
        errors={errors}
        options={data.shopifyProduct ? [data.shopifyProduct.id] : []}
        disabled={(!data.shopify_product_id && !data.shopifyProduct) || isDisabled}
      />

      <Button
        type="submit"
        variant="contained"
        sx={{ alignSelf: 'flex-end', mt: 'auto' }}
        disabled={isDisabled || isLoading || (!isDirty && photosToSave === null)}
        startIcon={isLoading ? <CircularProgress size={14} /> : null}
      >
        Save
      </Button>
    </Stack>
  )
}

export const ModelDetails = ({ data }: { data: ModelListRowModel }) => {
  const [photosToSave, setPhotosToSave] = useState<PhotosToSave | null>(null)

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={7}>
        <ModelPhotos data={data} setPhotosToSave={setPhotosToSave} />
      </Grid2>
      <Grid2 size={5}>
        <Tile
          caption="Status"
          text={
            <StatusChip
              status={{ type: 'model', value: data.status_v2 }}
              warnings={data.warnings}
              isLoading={false}
            />
          }
        />
        <Tile caption="Stock code" text={data.stock_code ?? 'N/A'} />

        <Tile caption="Material" text={data.material ?? 'N/A'} />
        <Tile caption="Type" text={data.product_type ?? 'N/A'} />
        <Tile caption="Supplier" text={data.supplier.full_name ?? 'N/A'} />
      </Grid2>
      <Grid2 size={12}>
        <ModelDetailForm
          data={data}
          photosToSave={photosToSave}
          resetPhotosToSave={() => setPhotosToSave(null)}
          isDisabled={false}
        />
      </Grid2>
    </Grid2>
  )
}
